<template>
  <div class="share-button-container">
    <button 
      class="share-button" 
      @click="handleShare"
      :disabled="disabled"
    >
      <div class="button-content">
        <div class="button-text">{{ defaultText }}</div>
        <div class="share-icon">
          <BIconBoxArrowUp class="icon-svg" />
        </div>
      </div>
    </button>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator'
import { BIconBoxArrowUp } from 'bootstrap-vue'
import { HistoryAlbumKeyedData } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumKeyedData'
import { HistoryAlbumUser } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumUser'
import { Client, defaultNativeInterfaceClient } from '@/utils/NativeInterface'
import { getLanguage, LANG_JA } from '@/i18n'

@Component({
  components: {
    BIconBoxArrowUp
  }
})
export default class ShareButton extends Vue {
  @Prop({ type: Boolean, default: false })
  disabled!: boolean

  @Prop({ type: String, default: '' })
  text!: string

  @Prop({ type: Number, required: true })
  year!: number

  @Prop({ type: Object, default: null })
  yearData!: HistoryAlbumKeyedData | null

  @Prop({ type: Object, default: null })
  user!: HistoryAlbumUser | null

  // デフォルトテキストを取得
  get defaultText(): string {
    return this.text || (this.$t('memoriesShareText1') as string)
  }

  // 日本語かどうかを判定
  get isJapanese(): boolean {
    return getLanguage() === LANG_JA
  }

  // ネイティブインターフェースが使用可能かどうか
  get canUserNativeInterface(): boolean {
    const nativeInterface = defaultNativeInterfaceClient()
    return nativeInterface.client !== Client.native
  }

  // シェアボタンクリック処理
  handleShare() {
    if (this.disabled) return
    
    const text = this.getShareText()
    let url = new URL(document.URL)
    url.searchParams.set('adj_t', '8ogcewh_z9yhix5')
    if (this.isJapanese) {
      url.searchParams.set('lang', 'ja')
    }
    
    if (this.canUserNativeInterface) {
      try {
        const nativeInterface = defaultNativeInterfaceClient()
        nativeInterface.sendEvent('invitation', 'share', {
          text: text,
          linkUrl: url.toString(),
        })
      } catch (e) {
        // フォールバック: 親コンポーネントにシェアイベントを送信
        this.$emit('share', { text, url: url.toString() })
      }
    } else {
      // 親コンポーネントにシェアイベントを送信
      this.$emit('share', { text, url: url.toString() })
    }
  }

  // シェアテキストを生成
  getShareText(): string {
    if (!this.yearData || !this.yearData.myRecord ||  this.yearData.myRecord.totalBroadcastHour === 0) {
      return this.isJapanese 
        ? '#REALITY思い出アルバム #REALITY'
        : '#REALITYMemories #REALITY'
    }

    // 最も多く受け取ったギフトを取得
    const topGift = this.getTopReceivedGift()
    // 最も人気だった配信を取得
    const topStream = this.getTopStream()

    let text = ''
    
    if (this.isJapanese) {
      text += `${this.year}年！\n`
      if (topGift.name && topGift.count > 0) {
        text += `${topGift.name}を ${topGift.count}件 もらいました！\n`
      }
      if (topStream) {
        text += `最も人気だった配信は ${topStream}！\n`
      }
      text += `\n#REALITY思い出アルバム #REALITY`
    } else {
      text += `Wow! In ${this.year}`
      if (topGift.name && topGift.count > 0) {
        text += `, I got ${topGift.name} ${topGift.count} times!`
      }
      text += `\n`
      if (topStream) {
        text += `My top stream of the year was ${topStream}!\n`
      }
      text += `\n#REALITYMemories #REALITY`
    }

    return text
  }

  // 最も多く受け取ったギフトを取得
  getTopReceivedGift(): { name: string; count: number } {
    if (!this.yearData || !this.yearData.receiveGiftRecords || this.yearData.receiveGiftRecords.length === 0) {
      return { name: '', count: 0 }
    }

    // データはすでにソートされているので最初の要素を取得
    const topGift = this.yearData.receiveGiftRecords
      .filter(record => record.gift)[0]

    return {
      name: topGift?.gift?.name || '',
      count: topGift?.totalCount || 0
    }
  }

  // 最も人気だった配信を取得
  getTopStream(): string {
    if (!this.yearData || !this.yearData.broadcastRecords || this.yearData.broadcastRecords.length === 0) {
      return ''
    }

    // データはすでにソートされているので最初の要素を取得
    const topStream = this.yearData.broadcastRecords
      .filter(record => record.liveName)[0]

    return topStream?.liveName || ''
  }
}
</script>

<style scoped>
.share-button-container {
  position: relative;
  display: inline-block;
}

.share-button {
  background: linear-gradient(to right, #a989fe 0%, #5ec3ff 50%, #54f4f1 100%);
  position: relative;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  display: flex;
  align-items: center;
  padding: 0;
}

.share-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.share-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 16px;
  gap: 4px;
  justify-content: flex-start;
}

.button-text {
  font-weight: 600;
  line-height: normal;
  font-style: normal;
  color: #ffffff;
  font-size: 12px;
  text-align: center;
  white-space: nowrap;
  display: flex;
  align-items: center;
}

.share-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-svg {
  width: 24px;
  height: 24px;
  display: block;
  color: white;
}

/* レスポンシブ対応 */
@media (max-width: 375px) {
  .button-text {
    font-size: 11px;
  }
  
  .share-icon {
    width: 20px;
    height: 20px;
  }
}
</style>
